import { Link, Navigate, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Button from '@/components/Button';
import Contact from '@/components/Contact';
import Gallery from '@/components/Gallery';
import JsonLd from '@/components/JsonLd';
import Process from '@/components/Process';
import Reveal from '@/components/Reveal';
import Section, { SectionHeading, Container } from '@/components/Section';
import AnchorLink from '@/components/AnchorLink';
import usePageMeta, { SITE_URL } from '@/hooks/usePageMeta';
import { SERVICES, getService } from '@/data/services';
import { useQuote } from '@/components/QuoteDialog';

function Breadcrumb({ title }) {
  return (
    <nav aria-label="Putanja" className="text-sm text-white/70">
      <ol className="flex flex-wrap items-center gap-1.5">
        <li><Link to="/" className="hover:text-white">Početna</Link></li>
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        <li><AnchorLink href="#usluge" className="hover:text-white">Usluge</AnchorLink></li>
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        <li aria-current="page" className="text-white">{title}</li>
      </ol>
    </nav>
  );
}

export default function ServicePage() {
  const { slug } = useParams();
  const service = getService(slug);
  const { openQuote } = useQuote();

  usePageMeta({
    title: service?.metaTitle,
    description: service?.metaDescription,
    path: service?.path,
  });

  if (!service) return <Navigate to="/" replace />;

  const others = SERVICES.filter((s) => s.id !== service.id);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: service.title,
      description: service.description,
      serviceType: service.title,
      url: `${SITE_URL}${service.path}/`,
      provider: { '@id': `${SITE_URL}/#business` },
      areaServed: { '@type': 'City', name: 'Sarajevo' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Početna', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Usluge', item: `${SITE_URL}/#usluge` },
        { '@type': 'ListItem', position: 3, name: service.title, item: `${SITE_URL}${service.path}/` },
      ],
    },
  ];

  return (
    <>
      <JsonLd data={schema} />

      <section className="on-dark bg-ink text-white pt-32 pb-16 sm:pt-40 sm:pb-24">
        <Container>
          <Breadcrumb title={service.title} />
          <div className="mt-10 grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">{service.h1}</h1>
              <p className="mt-6 max-w-xl text-lg sm:text-xl leading-relaxed text-white/85">{service.description}</p>
              <Button onClick={() => openQuote(service.id)} data-testid="service-cta" className="mt-10">
                Zatražite ponudu
              </Button>
            </div>
            <div className="aspect-[4/3] overflow-hidden bg-white/5">
              <img src={service.image} alt={service.alt} fetchPriority="high" className="h-full w-full object-cover" />
            </div>
          </div>
        </Container>
      </section>

      <Section tone="white">
        <SectionHeading eyebrow="Usluga" title="Šta izrađujemo" />
        <Reveal>
          <ul className="mt-12 grid gap-x-12 sm:grid-cols-2 lg:grid-cols-3 border-t border-ink/10">
            {service.examples.map((ex) => (
              <li key={ex} className="flex items-center gap-4 border-b border-ink/10 py-5 text-lg">
                <span className="h-px w-6 shrink-0 bg-brand" aria-hidden="true" />
                <span className="first-letter:uppercase">{ex}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </Section>

      <Section tone="dark">
        <SectionHeading tone="dark" eyebrow="Portfolio" title={`Primjeri naših radova`} text={`Fotografije stvarnih projekata iz područja: ${service.title.toLowerCase()}.`} />
        <Gallery key={service.id} filter={service.filter} />
      </Section>

      <Process />

      <Section tone="white">
        <SectionHeading eyebrow="Ostale usluge" title="Pogledajte i ostala područja rada." />
        <ul className="mt-12 grid gap-x-12 border-t border-ink/10 sm:grid-cols-2 lg:grid-cols-3">
          {others.map((s) => (
            <li key={s.id} className="border-b border-ink/10">
              <Link to={s.path} className="group flex h-full items-center justify-between gap-4 py-5 hover:text-brand transition-colors">
                <span className="text-lg font-semibold">{s.title}</span>
                <ChevronRight className="h-5 w-5 shrink-0 text-brand transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </Section>

      <Contact />
    </>
  );
}
